import { useContext, useEffect, useState } from 'react'
import { AndiUserProfile, BusinessUnit, ListOption } from '../../../../types/andTypes'
import {
  isTermWithinAnyWord,
  createDisplayString,
  idToNameString
} from '../../../../utilities/utils'
import {
  AvailableSeatCounterIcon,
  IconContainer
} from '../../../modal/popup/group-booking-popup/group-booking-popup-styles'
import SingleAndi from '../../../modal/popup/group-booking-popup/assets/singleAndi.svg'
import {
  DropDownContainer,
  ComboText,
  Input,
  DropDownSelected,
  DropDownList,
  DropDownItem,
  Label,
  Background,
  BookingComboInput
} from '../combobox-styles'
import { TextWithIcon, TextWithoutIcon } from './group-booking-combobox-styles'
import { handleAndisByBusinessUnit } from './utils'
import { ApiContext } from '../../../../contexts/api-context'

type GroupBookingComboBoxProps = {
  listItems: ListOption[]
  listValue?: string
  setListValue: (listValue: string) => void
  returnSelectedItem?: (SelectedItem: ListOption) => void
  valueProperty?: string
  displayTemplate?: string
  displayProperty?: string
  label?: string
  style?: React.CSSProperties
  placeholder?: string
  disabled: boolean
  errorHandle?: (error: string) => void
  stickySearch?: boolean
  selectedMembers: AndiUserProfile[]
  setSelectedMembers: (selectedMembers: AndiUserProfile[]) => void
  organiser?: AndiUserProfile
  selectedBu?: BusinessUnit | undefined
}

const GroupBookingComboBox = ({
  listItems,
  listValue,
  setListValue,
  returnSelectedItem,
  valueProperty = 'value',
  displayTemplate,
  displayProperty = 'displayValue',
  label,
  style,
  placeholder,
  disabled,
  stickySearch = true,
  selectedMembers,
  setSelectedMembers,
  organiser,
  errorHandle,
  selectedBu
}: GroupBookingComboBoxProps) => {
  const { userProfile } = useContext(ApiContext)
  const selectedItem = listItems.find(item => item[valueProperty] === listValue)
  const displayValue = createDisplayString(selectedItem, displayProperty, displayTemplate)
  const [buSearch, setBuSearch] = useState(displayValue)
  const [toggleMenu, setToggleMenu] = useState(false)
  const [displayIcon, setDisplayIcon] = useState<string[]>([])
  const [isUserSearching, setIsUserSearching] = useState<boolean>(false)
  const [buAndis, setBuAndis] = useState<ListOption[]>([])

  useEffect(() => {
    if (returnSelectedItem) returnSelectedItem(selectedItem as ListOption)
    if (!!organiser?.userId && !displayIcon.includes(idToNameString(organiser?.userId))) {
      setDisplayIcon([...displayIcon, idToNameString(organiser?.userId)])
    }
  }, [selectedItem])

  useEffect(() => {
    setBuSearch(displayValue)
  }, [])

  useEffect(() => {
    handleAndisByBusinessUnit(
      listItems,
      userProfile?.businessUnit || '',
      selectedBu?.id || '',
      setBuAndis
    )
  }, [selectedBu])

  useEffect(() => {
    // to trigger menu close
  }, [toggleMenu, displayIcon, selectedMembers])

  const resetBuSearch = () => {
    setIsUserSearching(false)
    stickySearch
      ? setBuSearch(createDisplayString(selectedItem, displayProperty, displayTemplate))
      : setBuSearch('')
  }

  const handleChange = (event: { target: { value: string } }) => {
    const searchString = event.target.value

    setIsUserSearching(event.target.value.length > 0)
    setBuSearch(searchString)
  }

  const displayItem = (item: ListOption) => {
    const itemValue = item[valueProperty] as string
    const itemDisplayValue = createDisplayString(item, displayProperty, displayTemplate)
    const isItemDisabled = !!item.disabled || itemValue === organiser?.userId
    const isSquad = item.noOfMembers
    const displayString = isSquad
      ? `${itemDisplayValue} (${item.noOfMembers} members)`
      : itemDisplayValue

    const manageIcon = (displayIcon: string[]) => {
      if (!!organiser?.userId && !displayIcon.includes(idToNameString(organiser?.userId))) {
        setDisplayIcon([...displayIcon, idToNameString(organiser?.userId)])
      }

      if (
        displayIcon?.includes(itemDisplayValue) &&
        (selectedMembers?.some(members => members.name === itemDisplayValue) ||
          selectedMembers?.some(members => members.team === itemDisplayValue))
      ) {
        const newDisplayIcon = displayIcon.filter(user => user !== itemDisplayValue)
        setDisplayIcon([...newDisplayIcon])
        const newSelectedMembers = selectedMembers.filter(user => user.name !== itemDisplayValue)
        setSelectedMembers([...newSelectedMembers])

        if (selectedMembers?.some(members => members.team === itemDisplayValue)) {
          const newSelectedMembers = selectedMembers.filter(
            user =>
              user.team !== itemDisplayValue ||
              (!!organiser?.userId && user.name === idToNameString(organiser?.userId))
          )
          setSelectedMembers([...newSelectedMembers])
          if (errorHandle) {
            errorHandle('REMOVE_BOOKING_ERROR')
          }
        }
      } else {
        setDisplayIcon([...displayIcon, itemDisplayValue])
        setListValue(itemValue)
        if (selectedMembers?.some(members => members.team === itemDisplayValue)) {
          const newDisplay = listItems
            .filter(user => user.team === itemDisplayValue)
            .map(member => member.name) as string[]
          setDisplayIcon([...displayIcon, itemDisplayValue, ...newDisplay])
        }
      }
    }

    if (!isTermWithinAnyWord(itemDisplayValue, buSearch)) return null
    return (
      itemValue &&
      itemValue !== listValue && (
        <DropDownItem
          data-testid={'dropdown-item'}
          key={itemValue}
          role="listitem"
          onClick={() => {
            if (isItemDisabled) return
            manageIcon(displayIcon)
          }}
          isDisabled={isItemDisabled}
        >
          {selectedMembers?.some(members => members.name === itemDisplayValue) ||
          (displayIcon?.includes(itemDisplayValue) &&
            selectedMembers?.some(members => members.team === itemDisplayValue)) ? (
            <TextWithIcon>
              <IconContainer>
                {
                  <AvailableSeatCounterIcon
                    data-testid={`seat-icon-${itemDisplayValue}`}
                    src={SingleAndi}
                  />
                }
              </IconContainer>
              <ComboText
                isDisabled={isItemDisabled}
                aria-label={`${itemDisplayValue}`}
                data-testid={'dropdown-item-value'}
              >
                {displayString}
              </ComboText>
            </TextWithIcon>
          ) : (
            <TextWithoutIcon>
              <ComboText
                isDisabled={isItemDisabled}
                aria-label={`${itemDisplayValue}`}
                data-testid={'dropdown-item-value'}
              >
                <span className="select-members">{displayString}</span>
              </ComboText>
            </TextWithoutIcon>
          )}
        </DropDownItem>
      )
    )
  }

  const listItemsDisplay = isUserSearching ? listItems.map(displayItem) : buAndis.map(displayItem)

  const results = listItemsDisplay.filter(i => i !== null).length

  useEffect(() => {
    if (errorHandle && !disabled) {
      if (results === 0 && buSearch.length > 3) errorHandle("Sorry! this user doesn't exist")
      else if (buSearch.length === 0) errorHandle('')
    }
  }, [])

  return (
    <>
      <Background
        data-testid="dropdown-background"
        isOpen={toggleMenu}
        onClick={() => {
          toggleMenu && setToggleMenu(!toggleMenu)
          resetBuSearch()
        }}
      />
      {label && <Label data-testid="dropdown-label">{label}</Label>}
      <DropDownContainer style={style}>
        <BookingComboInput
          role="button"
          onClick={() => {
            setToggleMenu(true)
            displayValue === buSearch && setBuSearch('')
          }}
        >
          <Input
            data-testid="text-input"
            placeholder={placeholder}
            type="text"
            value={buSearch}
            onChange={handleChange}
            disabled={false}
          />
          {toggleMenu ? <ComboText>&#9650;</ComboText> : <ComboText>&#9660;</ComboText>}
        </BookingComboInput>
        {toggleMenu && results !== 0 ? (
          <DropDownList data-testid="dropdown-list" role="list">
            <DropDownSelected>
              <ComboText>{displayValue}</ComboText>
            </DropDownSelected>
            {listItemsDisplay}
          </DropDownList>
        ) : null}
      </DropDownContainer>
    </>
  )
}

export default GroupBookingComboBox
